import React from 'react';
import PropTypes from 'prop-types';


function isListLine(line) {
  return /^\s*([A-Z]|\d{1,2})\s*[–-]\s+/.test(line);
}

function isJsonStart(line) {
  return /^\s*[\{\[]/.test(line);
}

function splitParagraphs(text) {
  if (text === undefined || text === null) return [];
  const normalized = String(text).replace(/\r\n/g, '\n').trim();
  return normalized.split(/\n\s*\n/).map(p => p.trim()).filter(p => p.length > 0);
}

function linkifyText(text) {
  if (!text) return text;

  const parts = [];
  let lastIndex = 0;

  const regex = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})|(https?:\/\/[^\s]+)/g;
  let match;

  while ((match = regex.exec(text)) !== null) {
    const start = match.index;

    if (start > lastIndex) {
      parts.push(text.substring(lastIndex, start));
    }

    if (match[1]) {
      const email = match[1];
      parts.push(
        <a
          key={start}
          href={`mailto:${email}`}
          style={{ fontWeight: 'bold', color: '#000' }}
        >
          {email}
        </a>
      );
    } else if (match[2]) {
      const url = match[2];
      parts.push(
        <a key={start} href={url} target="_blank" rel="noopener noreferrer">
          {url}
        </a>
      );
    }

    lastIndex = start + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts.length === 0 ? text : parts;
}


export default function RichTextRenderer({ text }) {
  if (text === undefined || text === null || String(text).trim() === '') return null;
  const paragraphs = splitParagraphs(text);

  return (
    <div style={{ width: '100%', textAlign: 'left' }}>
      {paragraphs.map((para, pIdx) => {
        const lines = para.split(/\n/).map(l => l.trim());
        if (lines.length > 0 && isJsonStart(lines[0])) {
          return (
            <pre
              key={pIdx}
              style={{
                background: '#f5f5f5',
                padding: 12,
                borderRadius: 4,
                overflowX: 'auto',
                fontSize: 13,
                whiteSpace: 'pre-wrap'
              }}
            >
              <code>{para}</code>
            </pre>
          );
        }

        const listLines = lines.filter(isListLine);
        if (listLines.length === lines.length && listLines.length > 0) {
          return (
            <ul key={pIdx} style={{ margin: '8px 0 16px 20px' }}>
              {lines.map((ln, i) => {
                const parts = ln.split(/[–-]\s+/);
                const label = (parts[0] || '').trim();
                const rest = (parts[1] || '').trim();
                return (
                  <li key={i} style={{ marginBottom: 6 }}>
                    <strong style={{ marginRight: 8 }}>{label}</strong>
                    <span>{linkifyText(rest)}</span>
                  </li>
                );
              })}
            </ul>
          );
        }

        const firstLine = lines[0] || '';
        const labelMatch = firstLine.match(/^(Soru:|Cevap:)\s*(.*)/i);

        if (labelMatch) {
          const label = labelMatch[1];
          const restOfFirst = labelMatch[2];
          const rest = [restOfFirst, ...lines.slice(1)].join('\n');
          return (
            <div key={pIdx} style={{ marginBottom: 12 }}>
              <div style={{ fontWeight: 'bold', marginBottom: 6 }}>{label}</div>
              <div style={{ whiteSpace: 'pre-line', lineHeight: 1.5 }}>{linkifyText(rest)}</div>
            </div>
          );
        }

        return (
          <p key={pIdx} style={{ margin: '8px 0', lineHeight: 1.6, whiteSpace: 'pre-line' }}>
            {linkifyText(para)}
          </p>
        );
      })}
    </div>
  );
}

RichTextRenderer.propTypes = {
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])])
};

RichTextRenderer.defaultProps = {
  text: ''
};
